import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import { Link, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ShieldCheck, ScrollText, Database, CreditCard,
  Mail, Cog, RefreshCw, Download, Trash2, ChevronRight, Loader2,
  ArrowUpRight, CheckCircle2, AlertTriangle, TrendingUp, Activity, HardDrive,
  UserCheck, X, MessageSquare, ExternalLink, Radar, Lock, Info, Filter,
  Folders, Search,
} from "lucide-react";

const TABS = [
  { key: "cockpit",   icon: Radar,           label: "Cockpit stratégique" },
  { key: "dossiers",  icon: Folders,         label: "Tous les dossiers" },
  { key: "overview",  icon: LayoutDashboard, label: "Vue d'ensemble" },
  { key: "vetting",   icon: UserCheck,       label: "Validation pros" },
  { key: "users",     icon: Users,           label: "Utilisateurs & rôles" },
  { key: "audit",     icon: ScrollText,      label: "Journal d'audit" },
  { key: "backups",   icon: Database,        label: "Sauvegardes" },
  { key: "billing",   icon: CreditCard,      label: "Facturation" },
  { key: "system",    icon: Cog,             label: "Système" },
];

export default function AdminConsole() {
  const { user, loading } = useAuth();
  const { roles: effectiveRoles, impersonatingAs } = usePermissions();
  const [tab, setTab] = useState("cockpit");

  // Client-side RBAC guard — only founders/admins can view the console.
  // When impersonating a non-admin persona, hide the admin shell.
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] flex items-center justify-center" data-testid="admin-console-loading">
        <Loader2 className="h-4 w-4 animate-spin text-[#0B2545]" strokeWidth={1.5} />
      </div>
    );
  }
  const roles = new Set(effectiveRoles || []);
  const canAccess = roles.has("founder") || roles.has("admin") || roles.has("super_admin");
  if (!canAccess) {
    return <Navigate to="/app" replace />;
  }
  // Expose impersonation status for downstream conditional UI (unused for now)
  void impersonatingAs;

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="admin-console">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10">
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy">Console administrateur</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-navy tracking-tight leading-[1.05]">Le pouls d&apos;ALTIORA</h1>
        </div>

        <div className="flex gap-1 border-b border-[#E4E1D8] mb-6 overflow-x-auto">
          {TABS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 whitespace-nowrap transition ${
                tab === key ? "border-[#0B2545] text-[#0B2545]" : "border-transparent text-[#5B5B62] hover:text-[#1F2937]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {tab === "cockpit"  && <CockpitTab />}
        {tab === "dossiers" && <DossiersTab />}
        {tab === "overview" && <OverviewTab />}
        {tab === "vetting"  && <VettingTab />}
        {tab === "users"    && <UsersTab />}
        {tab === "audit"    && <AuditTab />}
        {tab === "backups"  && <BackupsTab />}
        {tab === "billing"  && <BillingTab />}
        {tab === "system"   && <SystemTab />}
      </div>
    </div>
  );
}

// ================================================================
// COCKPIT STRATÉGIQUE — M7 Cohortes anonymisées + KPI plateforme
// ================================================================
function CockpitTab() {
  const [platform, setPlatform] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: "", sector: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [dash, coh] = await Promise.all([
        api.get("/admin/metrics/overview").catch(() => ({ data: null })),
        api.get("/diagnostic/cohort/territorial").catch(() => ({ data: null })),
      ]);
      setPlatform(dash.data);
      setCohort(coh.data);
    } catch (err) {
      altioraToast.error("Impossible de charger le cockpit.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyFilters = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.city) params.set("city", filters.city);
      if (filters.sector) params.set("sector", filters.sector);
      const { data } = await api.get(`/diagnostic/cohort/territorial?${params.toString()}`);
      setCohort(data);
    } catch (err) {
      altioraToast.error("Filtrage impossible.");
    }
  };

  if (loading) return <div className="py-10"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-10" data-testid="cockpit-strategique">
      {/* Header narratif */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Radar className="h-4 w-4 text-[#7A5B14]" />
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            ALTIORA Méthode™ · Moteur M7 · Intelligence territoriale
          </div>
        </div>
        <h2 className="font-display text-3xl md:text-4xl text-navy leading-tight max-w-3xl">
          Voir la plateforme comme un système vivant.
        </h2>
        <p className="mt-4 text-base text-[#374151] max-w-2xl leading-relaxed">
          Les cohortes sont agrégées, anonymisées, et respectent une k-anonymité ≥ 20.
          Aucun individu identifiable n&apos;apparaît ici — c&apos;est ce qui rend ce cockpit publiable
          auprès de partenaires institutionnels.
        </p>
      </motion.div>

      {/* KPI plateforme */}
      {platform && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.15, duration: 0.6 } }}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-[#8B8B8F] mb-4">KPI plateforme</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile
              label="Membres inscrits"
              value={platform?.users?.total ?? "—"}
              sub={platform?.users?.mau != null ? `${platform.users.mau} actifs 30j` : null}
              testid="cockpit-kpi-members"
            />
            <KpiTile
              label="Diagnostics complétés"
              value={cohort?.answer?.n ?? "—"}
              sub="cohorte anonymisée M7"
              testid="cockpit-kpi-diags"
            />
            <KpiTile
              label="Nouveaux · 7 jours"
              value={platform?.users?.new_7d ?? "—"}
              sub={platform?.users?.new_30d != null ? `${platform.users.new_30d} sur 30j` : null}
              testid="cockpit-kpi-new"
            />
            <KpiTile
              label="Contenus réseau"
              value={platform?.network?.communities ?? platform?.network?.total ?? "—"}
              sub={platform?.network?.posts_7d != null ? `${platform.network.posts_7d} posts · 7j` : "activité récente"}
              testid="cockpit-kpi-network"
            />
          </div>
        </motion.div>
      )}

      {/* M7 — Cohortes anonymisées */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.6 } }}
        className="border-2 border-[#0B2545]/20 bg-white rounded-xl p-6 md:p-8"
        data-testid="cockpit-m7"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-3.5 w-3.5 text-emerald-700" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                Anonymisé · k ≥ 20
              </div>
            </div>
            <h3 className="font-display text-2xl text-navy leading-tight">
              Radar territorial IMES™
            </h3>
            <p className="mt-2 text-sm text-[#5B5B62] leading-relaxed">
              Filtrez par ville ou secteur. Si la cohorte est &lt; 20 individus, aucune donnée
              n&apos;est révélée — c&apos;est la garantie d&apos;anonymat.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border-2 border-[#E5E7EB] text-[#374151] hover:border-navy hover:text-navy px-3 py-2 text-xs font-medium rounded-md transition"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mb-6">
          <input
            type="text"
            placeholder="Ville (ex: Montréal)"
            value={filters.city}
            onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
            data-testid="cockpit-filter-city"
            className="px-4 py-2.5 border border-[#E4E1D8] bg-white rounded-md text-sm focus:border-navy outline-none"
          />
          <input
            type="text"
            placeholder="Secteur (ex: tech)"
            value={filters.sector}
            onChange={(e) => setFilters((f) => ({ ...f, sector: e.target.value }))}
            data-testid="cockpit-filter-sector"
            className="px-4 py-2.5 border border-[#E4E1D8] bg-white rounded-md text-sm focus:border-navy outline-none"
          />
          <button
            onClick={applyFilters}
            data-testid="cockpit-filter-apply"
            className="inline-flex items-center justify-center gap-2 bg-navy text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#123163] transition"
          >
            <Filter className="h-3.5 w-3.5" /> Appliquer
          </button>
        </div>

        {/* Résultats cohorte */}
        {cohort ? (
          cohort.answer?.cohort_available ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cockpit-cohort-results">
              <CohortMetric
                label="Taille de cohorte"
                value={cohort.answer.n}
                sub="individus anonymisés"
              />
              <CohortMetric
                label="IMES moyen"
                value={cohort.answer.avg_imes}
                sub="/100"
                highlight
              />
              <CohortMetric
                label="IMES médian"
                value={cohort.answer.median_imes}
                sub="/100"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg p-6 text-center" data-testid="cockpit-cohort-empty">
              <Lock className="h-8 w-8 text-amber-700 mx-auto mb-3" />
              <p className="text-sm text-amber-900 font-medium">
                Cohorte trop petite — anonymat préservé
              </p>
              <p className="text-xs text-amber-800 mt-1">
                {cohort.answer?.reason_fr || "Cohorte < 20."}
              </p>
            </div>
          )
        ) : (
          <div className="text-sm text-[#5B5B62]">Chargement des cohortes…</div>
        )}

        {/* Trusted Intelligence envelope */}
        {cohort && (
          <div className="mt-6 p-4 bg-[#F1EFE9] rounded-lg border border-[#E4E1D8]">
            <div className="flex items-start gap-3">
              <Info className="h-4 w-4 text-navy mt-0.5 flex-shrink-0" />
              <div className="text-xs text-[#374151] leading-relaxed">
                <div className="font-mono uppercase tracking-widest text-[#7A5B14] text-[10px] mb-1">
                  Trusted Intelligence™
                </div>
                <div><strong className="text-navy">Pourquoi :</strong> {cohort.why_fr}</div>
                <div className="mt-1"><strong className="text-navy">Confiance :</strong> {Math.round((cohort.confidence || 0) * 100)}%</div>
                <div className="mt-1"><strong className="text-navy">Sources :</strong> {(cohort.sources_fr || []).join(" · ")}</div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Actif intellectuel */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.45, duration: 0.6 } }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <ActifTile
          title="ALTIORA Méthode™"
          desc="Doctrine IMES v2 ratifiée. Prête pour publication et licence institutionnelle."
          status="Ratifiée · 2026-02-08"
        />
        <ActifTile
          title="Contrat Trusted Intelligence™"
          desc="Chaque score est explicable — auditable par un chercheur ou un régulateur."
          status="Actif sur 8 moteurs"
        />
        <ActifTile
          title="Livre blanc"
          desc="40 pages FR/EN — chapitres §12 doctrine. À rédiger en Phase 4."
          status="Brief prêt"
        />
      </motion.div>
    </div>
  );
}

function KpiTile({ label, value, sub, testid }) {
  return (
    <div className="border-2 border-[#E4E1D8] bg-white rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl text-navy tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#5B5B62]">{sub}</div>}
    </div>
  );
}

function CohortMetric({ label, value, sub, highlight = false }) {
  return (
    <div className={`rounded-lg p-4 border ${highlight ? "border-navy bg-[#F5F4F0]" : "border-[#E4E1D8] bg-white"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className="mt-2 font-display text-4xl text-navy tabular-nums">
        {value}<span className="text-lg text-[#8B8B8F] ml-1">{sub}</span>
      </div>
    </div>
  );
}

function ActifTile({ title, desc, status }) {
  return (
    <div className="border-2 border-[#E4E1D8] bg-white rounded-lg p-5">
      <div className="font-display text-lg text-navy leading-tight">{title}</div>
      <p className="mt-2 text-xs text-[#5B5B62] leading-relaxed">{desc}</p>
      <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
        <CheckCircle2 className="h-3 w-3" /> {status}
      </div>
    </div>
  );
}


// ================================================================
// DOSSIERS — Admin global view (off-record read across all dossiers)
// Consumes GET /api/dossiers/?all_dossiers=true — restricted to founder/admin.
// ================================================================
function DossiersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterElia, setFilterElia] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dossiers/", {
        params: { all_dossiers: true, include_archived: includeArchived },
      });
      setRows(data.items || []);
      setError("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Chargement impossible");
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [includeArchived]);

  const eliaOptions = Array.from(new Set(rows.map((r) => r.elia_module).filter(Boolean))).sort();
  const statusOptions = Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort();

  const filtered = rows.filter((r) => {
    if (filterType && r.type !== filterType) return false;
    if (filterElia && r.elia_module !== filterElia) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = [
        r.name, r.dossier_id, r.owner_user_id, r.subject_user_id,
        r.subtype, r.elia_module, r.jurisdiction,
      ].filter(Boolean).map((s) => String(s).toLowerCase()).join(" ");
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const kpi = {
    total: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    pending: rows.filter((r) => r.status === "pending_client_approval").length,
    draft: rows.filter((r) => r.status === "draft").length,
    archived: rows.filter((r) => r.status === "archived").length,
    individual: rows.filter((r) => r.type === "individual").length,
    legal: rows.filter((r) => r.type === "legal_entity").length,
    proCreated: rows.filter((r) => r.created_by_pro_key).length,
  };

  return (
    <div data-testid="admin-dossiers-tab">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Off-record · Accès global fondateur</div>
        <h2 className="mt-2 font-display text-3xl text-navy tracking-tight">Tous les dossiers de la plateforme</h2>
        <p className="mt-2 text-sm text-[#5B5B62] max-w-2xl leading-relaxed">
          Vision complète de l&apos;ensemble des dossiers ALTIORA — tous types, tous propriétaires, tous statuts.
          Cette vue est réservée à la fondation ; aucune trace utilisateur n&apos;est laissée lors des consultations.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-6" data-testid="admin-dossiers-kpi">
        <DossierKpi label="Total" value={kpi.total} />
        <DossierKpi label="Actifs" value={kpi.active} tone="active" />
        <DossierKpi label="Attente client" value={kpi.pending} tone="warning" />
        <DossierKpi label="Brouillons" value={kpi.draft} />
        <DossierKpi label="Archivés" value={kpi.archived} tone="muted" />
        <DossierKpi label="Personnes" value={kpi.individual} />
        <DossierKpi label="Organisations" value={kpi.legal} />
        <DossierKpi label="Créés par Pro" value={kpi.proCreated} tone="gold" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8B8B8F]" strokeWidth={2} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher — nom, ID, propriétaire, sous-type…"
            data-testid="admin-dossiers-search"
            className="w-full rounded-md border border-[#E4E1D8] bg-white pl-9 pr-3 py-2 text-sm placeholder:text-[#B8B5AC] focus:outline-none focus:border-[#0B2545]"
          />
        </div>
        <SelectMini value={filterType} setValue={setFilterType} testId="admin-dossiers-filter-type"
          options={[["", "Tous types"], ["individual", "Personne"], ["legal_entity", "Organisation"]]} />
        <SelectMini value={filterElia} setValue={setFilterElia} testId="admin-dossiers-filter-elia"
          options={[["", "Tous modules ALTIORA"], ...eliaOptions.map((e) => [e, e])]} />
        <SelectMini value={filterStatus} setValue={setFilterStatus} testId="admin-dossiers-filter-status"
          options={[["", "Tous statuts"], ...statusOptions.map((s) => [s, s])]} />
        <label className="inline-flex items-center gap-2 text-xs text-[#5B5B62] cursor-pointer">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="admin-dossiers-include-archived" />
          Inclure archivés
        </label>
        <button
          onClick={load}
          data-testid="admin-dossiers-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:bg-[#F8F7F3] px-3 py-2 text-xs text-[#5B5B62]"
        >
          <RefreshCw className="h-3 w-3" strokeWidth={2} /> Rafraîchir
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-[#E11D48]/30 bg-[#FEF2F2] px-4 py-2 text-sm text-[#B91C1C]">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-[#8B8B8F] text-sm"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} /> Chargement des dossiers…</div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-[#8B8B8F] text-sm rounded-md border border-dashed border-[#E4E1D8]">
          Aucun dossier ne correspond aux filtres actuels.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#E4E1D8] bg-white" data-testid="admin-dossiers-table">
          <table className="min-w-full text-xs">
            <thead className="bg-[#F8F7F3] text-[#5B5B62]">
              <tr className="text-left">
                <Th>Nom</Th>
                <Th>Type</Th>
                <Th>Sous-type</Th>
                <Th>Module ALTIORA</Th>
                <Th>Statut</Th>
                <Th>Propriétaire</Th>
                <Th>Créé par</Th>
                <Th>Créé le</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.dossier_id} className="border-t border-[#F1EFE8] hover:bg-[#FBF6EA]/40 transition-colors" data-testid={`admin-dossier-row-${d.dossier_id}`}>
                  <Td>
                    <div className="font-medium text-[#1F2937]">{d.name}</div>
                    <div className="mt-0.5 text-[10px] font-mono text-[#8B8B8F]">{d.dossier_id}</div>
                  </Td>
                  <Td>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${d.type === "individual" ? "bg-[#0B2545]/10 text-[#0B2545]" : "bg-[#7A5B14]/10 text-[#7A5B14]"}`}>
                      {d.type === "individual" ? "Personne" : "Organisation"}
                    </span>
                  </Td>
                  <Td className="text-[#5B5B62]">{d.subtype || "—"}</Td>
                  <Td className="text-[#5B5B62]">{d.elia_module || "—"}</Td>
                  <Td><StatusBadge status={d.status} /></Td>
                  <Td className="font-mono text-[10px] text-[#5B5B62]">{d.owner_user_id?.slice(0, 12) || "—"}…</Td>
                  <Td>
                    {d.created_by_pro_key ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#7A5B14]/10 text-[#7A5B14] px-2 py-0.5 text-[10px] font-mono">Pro</span>
                    ) : (
                      <span className="text-[10px] text-[#8B8B8F]">User</span>
                    )}
                  </Td>
                  <Td className="text-[10px] text-[#5B5B62]">{fmtDate(d.created_at)}</Td>
                  <Td>
                    <Link
                      to={`/app/dossiers/${d.dossier_id}`}
                      target="_blank"
                      data-testid={`admin-dossier-open-${d.dossier_id}`}
                      className="inline-flex items-center gap-1 text-[#0B2545] hover:underline text-[11px]"
                    >
                      Ouvrir <ExternalLink className="h-2.5 w-2.5" strokeWidth={2} />
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[10px] font-mono text-[#8B8B8F]">
        {filtered.length} dossier{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur {rows.length} total.
      </div>
    </div>
  );
}

function DossierKpi({ label, value, tone = "neutral" }) {
  const toneStyles = {
    neutral: "text-[#1F2937] bg-white border-[#E4E1D8]",
    active: "text-[#065F46] bg-[#ECFDF5] border-[#065F46]/20",
    warning: "text-[#7A5B14] bg-[#FBF6EA] border-[#7A5B14]/20",
    muted: "text-[#8B8B8F] bg-[#F8F7F3] border-[#E4E1D8]",
    gold: "text-[#7A5B14] bg-[#FBF6EA] border-[#C5A880]/40",
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${toneStyles}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="mt-1 font-display text-2xl tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Actif", cls: "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/20" },
    draft: { label: "Brouillon", cls: "bg-white text-[#5B5B62] border-[#E4E1D8]" },
    pending_client_approval: { label: "Attente client", cls: "bg-[#FBF6EA] text-[#7A5B14] border-[#C5A880]/40" },
    archived: { label: "Archivé", cls: "bg-[#F8F7F3] text-[#8B8B8F] border-[#E4E1D8]" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-white text-[#5B5B62] border-[#E4E1D8]" };
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${m.cls}`}>{m.label}</span>;
}

function Th({ children }) {
  return <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-left">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
function SelectMini({ value, setValue, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => setValue(e.target.value)}
      data-testid={testId}
      className="rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-xs text-[#1F2937]"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

// ================================================================
// OVERVIEW — KPI dashboard
// ================================================================
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/metrics/overview");
      setData(r.data);
    } catch (e) {
      altioraToast.error("Impossible de charger les métriques");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-sm text-[#5B5B62]"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Chargement…</div>;

  const s = data.security || {};
  const iv = data.interview || {};
  const scoreStats = iv.score_stats;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#8B8B8F] font-mono">
          Mise à jour : {new Date(data.generated_at).toLocaleString()}
        </div>
        <button data-testid="overview-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#0B2545] hover:underline">
          <RefreshCw className="h-3 w-3" /> Actualiser
        </button>
      </div>

      <SectionTitle icon={TrendingUp} title="Adoption utilisateurs" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total utilisateurs" value={data.users?.total} sub={`${data.users?.suspended || 0} suspendus`} />
        <KPI label="DAU" value={data.users?.dau} sub="dernières 24h" />
        <KPI label="WAU" value={data.users?.wau} sub="dernière semaine" />
        <KPI label="MAU" value={data.users?.mau} sub="dernier mois" />
      </div>

      <SectionTitle icon={Activity} title="Production Studio & Interview" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Docs Studio" value={data.studio?.total_documents} sub={`+${data.studio?.docs_7d || 0} sur 7j`} />
        <KPI label="Simulations" value={iv.total_sessions} sub={`${iv.completed || 0} complétées`} />
        <KPI label="Taux complétion" value={`${iv.completion_rate || 0}%`} sub="Interview Prep" tone={iv.completion_rate >= 60 ? "good" : iv.completion_rate >= 30 ? "warn" : "bad"} />
        <KPI label="Score moyen" value={scoreStats ? `${scoreStats.avg}/100` : "—"} sub={scoreStats ? `min ${scoreStats.min} · max ${scoreStats.max}` : "aucun score encore"} />
      </div>

      <SectionTitle icon={ShieldCheck} title="Sécurité & auth (7 jours)" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Connexions réussies" value={s.login_success_7d || 0} />
        <KPI label="Échecs" value={s.login_failed_7d || 0} tone={(s.login_failed_7d || 0) > 20 ? "bad" : "good"} />
        <KPI label="Taux succès" value={`${s.success_rate_7d || 0}%`} tone={(s.success_rate_7d || 0) >= 95 ? "good" : "warn"} />
        <KPI label="Événements suspects" value={s.suspicious_7d || 0} tone={(s.suspicious_7d || 0) > 0 ? "warn" : "good"} />
      </div>

      <SectionTitle icon={Database} title="Réseau, contenu & facturation" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Écosystème entités" value={data.network?.ecosystem_entities || 0} />
        <KPI label="Sources UKE" value={data.network?.knowledge_sources || 0} />
        <KPI label="Newsletters" value={data.network?.newsletters_generated || 0} />
        <KPI label="Abonnements actifs" value={data.billing?.active_subscriptions || 0} />
      </div>

      <SectionTitle icon={Users} title="Répartition par rôle" />
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(data.users?.roles || []).map((r) => (
            <div key={r.role} className="text-center">
              <div className="text-2xl font-display text-[#1F2937]">{r.count}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mt-1">{r.role}</div>
            </div>
          ))}
        </div>
      </div>

      <SectionTitle icon={Activity} title="Types de documents produits" />
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
        {(data.studio?.by_type || []).length === 0 ? (
          <div className="text-sm text-[#8B8B8F]">Aucun document généré pour le moment.</div>
        ) : (
          <div className="space-y-2">
            {(data.studio?.by_type || []).slice(0, 8).map((t) => {
              const max = Math.max(...(data.studio?.by_type || []).map((x) => x.count), 1);
              const pct = Math.round((t.count / max) * 100);
              return (
                <div key={t.type}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#1F2937]">{t.type}</span>
                    <span className="font-mono text-[#5B5B62]">{t.count}</span>
                  </div>
                  <div className="mt-1 h-1 rounded bg-[#EBE8DE] overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#0B2545,#3C5C8C)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ================================================================
// USERS
// ================================================================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setUsers(r.data.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const suspend = async (u) => {
    if (!window.confirm(`Suspendre ${u.email} ?`)) return;
    setBusy(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`, { reason: "Action console admin" });
      altioraToast.success("Utilisateur suspendu");
      load();
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };
  const reactivate = async (u) => {
    setBusy(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/reactivate`);
      altioraToast.success("Compte réactivé");
      load();
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          data-testid="users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Rechercher par email ou nom…"
          className="flex-1 rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
        />
        <button onClick={load} className="rounded-md bg-[#0B2545] hover:bg-[#123163] text-white text-sm px-4 py-2">Chercher</button>
      </div>

      <div className="rounded-lg border border-[#E4E1D8] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1EFE9] text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            <tr>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Rôles</th>
              <th className="text-left px-4 py-2.5">État</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-[#EBE8DE]">
                <td className="px-4 py-3">
                  <div className="text-[#1F2937]">{u.email}</div>
                  <div className="text-[10px] text-[#8B8B8F] font-mono">{u.user_id?.slice(0, 20)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles || ["user"]).map((r) => (
                      <span key={r} className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#0B2545]/10 text-[#0B2545]">{r}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {u.suspended ? (
                    <span className="text-[10px] font-mono uppercase text-[#DC2626]">Suspendu</span>
                  ) : (
                    <span className="text-[10px] font-mono uppercase text-[#059669]">Actif</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.suspended ? (
                    <button
                      onClick={() => reactivate(u)}
                      disabled={busy === u.user_id}
                      data-testid={`user-reactivate-${u.user_id}`}
                      className="text-xs text-[#059669] hover:underline disabled:opacity-50"
                    >Réactiver</button>
                  ) : (
                    <button
                      onClick={() => suspend(u)}
                      disabled={busy === u.user_id}
                      data-testid={`user-suspend-${u.user_id}`}
                      className="text-xs text-[#DC2626] hover:underline disabled:opacity-50"
                    >Suspendre</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[#8B8B8F]">Aucun utilisateur trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#8B8B8F]">
        Pour modifier les rôles finement, utilisez le <Link to="/app/admin/users" className="text-[#0B2545] underline">tableau détaillé</Link>.
      </div>
    </div>
  );
}

// ================================================================
// AUDIT
// ================================================================
function AuditTab() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ action: "", actor_email: "", since_days: 7 });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.action) params.set("action", filter.action);
      if (filter.actor_email) params.set("actor_email", filter.actor_email);
      params.set("since_days", filter.since_days);
      params.set("limit", 100);
      const [e, s] = await Promise.all([
        api.get(`/admin/audit/events?${params.toString()}`),
        api.get(`/admin/audit/summary?since_days=${filter.since_days}`),
      ]);
      setEvents(e.data.items || []);
      setSummary(s.data);
    } catch { altioraToast.error("Erreur chargement audit"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Événements total</div>
          <div className="mt-1 text-2xl font-display text-[#1F2937]">{summary?.total_events || 0}</div>
          <div className="text-[10px] text-[#8B8B8F] mt-0.5">{summary?.window_days || 7} derniers jours</div>
        </div>
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-4 md:col-span-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-2">Actions les plus fréquentes</div>
          <div className="flex flex-wrap gap-1.5">
            {(summary?.top_actions || []).slice(0, 8).map((a) => (
              <button
                key={a.action}
                onClick={() => { setFilter({ ...filter, action: a.action }); setTimeout(load, 50); }}
                className="text-[10px] font-mono px-2 py-1 rounded bg-[#F1EFE9] hover:bg-[#E4E1D8] text-[#1F2937]"
              >
                {a.action} · <span className="text-[#5B5B62]">{a.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          data-testid="audit-filter-action"
          value={filter.action}
          onChange={(e) => setFilter({ ...filter, action: e.target.value })}
          placeholder="Action (ex : user.suspend, backup.*)"
          className="rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
        />
        <input
          data-testid="audit-filter-actor"
          value={filter.actor_email}
          onChange={(e) => setFilter({ ...filter, actor_email: e.target.value })}
          placeholder="Email de l'acteur"
          className="rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
        />
        <select
          value={filter.since_days}
          onChange={(e) => setFilter({ ...filter, since_days: Number(e.target.value) })}
          className="rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
        >
          <option value={1}>Dernières 24h</option>
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
        </select>
        <button onClick={load} disabled={loading} data-testid="audit-refresh" className="rounded-md bg-[#0B2545] hover:bg-[#123163] text-white text-sm px-4 py-2 disabled:opacity-50">
          {loading ? "Chargement…" : "Filtrer"}
        </button>
      </div>

      <div className="rounded-lg border border-[#E4E1D8] bg-white overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#F1EFE9] text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            <tr>
              <th className="text-left px-3 py-2">Horodatage</th>
              <th className="text-left px-3 py-2">Acteur</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Cible</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Résultat</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={i} data-testid={`audit-row-${i}`} className="border-t border-[#EBE8DE]">
                <td className="px-3 py-2 font-mono text-[#5B5B62]">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-3 py-2">
                  <div className="text-[#1F2937]">{e.actor_email || "—"}</div>
                  <div className="text-[9px] font-mono text-[#8B8B8F]">{e.actor_role || ""}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[#0B2545]">{e.action}</td>
                <td className="px-3 py-2">
                  <div className="text-[#1F2937]">{e.entity_type}</div>
                  <div className="text-[9px] font-mono text-[#8B8B8F]">{e.entity_id?.slice(0, 24)}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[#5B5B62]">{e.ip || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`text-[9px] font-mono uppercase ${e.outcome === "success" ? "text-[#059669]" : "text-[#DC2626]"}`}>
                    {e.outcome}
                  </span>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8B8B8F]">Aucun événement trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================================================================
// BACKUPS
// ================================================================
function BackupsTab() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/status/tool"),
      ]);
      setList(l.data.backups || []);
      setStatus(s.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/admin/backups");
      altioraToast.success(`Sauvegarde créée : ${r.data.name}`);
      load();
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec"); }
    finally { setCreating(false); }
  };

  const download = (name) => {
    // Direct GET with token in header — we let the browser handle via a blob fetch
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("altiora_token");
    fetch(`${backendUrl}/api/admin/backups/${name}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name; a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => altioraToast.error("Téléchargement impossible"));
  };

  const restore = async (name) => {
    const confirmed = window.prompt(`⚠️ ATTENTION : cette action va REMPLACER toute la base par la sauvegarde. Tapez le nom exact "${name}" pour confirmer.`);
    if (confirmed !== name) return;
    setRestoring(name);
    try {
      await api.post(`/admin/backups/${name}/restore`);
      altioraToast.success("Restauration terminée. Reconnectez-vous.");
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec restauration"); }
    finally { setRestoring(null); }
  };

  const del = async (name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await api.delete(`/admin/backups/${name}`);
      altioraToast.success("Sauvegarde supprimée");
      load();
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-[#1F2937]">Outillage</div>
            <div className="mt-1 text-xs text-[#5B5B62]">
              {status?.mongodump ? (
                <span className="inline-flex items-center gap-1 text-[#059669]"><CheckCircle2 className="h-3 w-3" /> mongodump/mongorestore prêts</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[#DC2626]"><AlertTriangle className="h-3 w-3" /> {status?.hint || "outil manquant"}</span>
              )}
              {status && <span className="ml-3">Rétention : {status.retention_days} jours</span>}
            </div>
          </div>
          <button
            onClick={create}
            disabled={creating || !status?.mongodump}
            data-testid="backups-create-btn"
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-50 text-white text-sm px-4 py-2"
          >
            {creating ? <><Loader2 className="h-4 w-4 animate-spin" /> Création…</> : <><HardDrive className="h-4 w-4" /> Créer une sauvegarde</>}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-[#E4E1D8] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1EFE9] text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            <tr>
              <th className="text-left px-4 py-2.5">Nom</th>
              <th className="text-left px-4 py-2.5">Taille</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.name} data-testid={`backup-row-${b.name}`} className="border-t border-[#EBE8DE]">
                <td className="px-4 py-3 font-mono text-[11px]">{b.name}</td>
                <td className="px-4 py-3 text-[#5B5B62]">{(b.size_bytes / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-3 text-[#5B5B62]">{new Date(b.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => download(b.name)} data-testid={`backup-download-${b.name}`} className="text-xs text-[#0B2545] hover:underline">
                    <Download className="h-3 w-3 inline mr-0.5" /> Télécharger
                  </button>
                  <button onClick={() => restore(b.name)} disabled={restoring === b.name} data-testid={`backup-restore-${b.name}`} className="text-xs text-[#C5A880] hover:underline disabled:opacity-50">
                    {restoring === b.name ? "Restauration…" : <><RefreshCw className="h-3 w-3 inline mr-0.5" /> Restaurer</>}
                  </button>
                  <button onClick={() => del(b.name)} data-testid={`backup-delete-${b.name}`} className="text-xs text-[#DC2626] hover:underline">
                    <Trash2 className="h-3 w-3 inline mr-0.5" /> Supprimer
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8B8B8F]">Aucune sauvegarde encore. Créez-en une ou attendez le cron quotidien.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================================================================
// BILLING
// ================================================================
function BillingTab() {
  const [cockpit, setCockpit] = useState(null);
  useEffect(() => {
    api.get("/finance/cockpit").then((r) => setCockpit(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
        <div className="text-sm text-[#5B5B62]">
          {cockpit ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="MRR" value={cockpit.mrr ? `${cockpit.mrr} $` : "—"} />
              <KPI label="Abonnements actifs" value={cockpit.active_subscriptions || 0} />
              <KPI label="Essais" value={cockpit.trialing || 0} />
              <KPI label="Impayés" value={cockpit.past_due || 0} />
            </div>
          ) : (
            <div className="text-[#8B8B8F]">Chargement des données facturation…</div>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
        <div className="text-xs text-[#5B5B62]">
          Le module Stripe est branché. La liste des transactions et l&apos;audit des paiements sont accessibles via l&apos;API <span className="font-mono text-[#0B2545]">/api/finance/transactions</span> et le portail Stripe (bouton dans le profil utilisateur).
        </div>
      </div>
    </div>
  );
}

// ================================================================
// SYSTEM — email, feature flags, links
// ================================================================
function SystemTab() {
  const [emailStatus, setEmailStatus] = useState(null);
  const [modules, setModules] = useState([]);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email/status").then((r) => setEmailStatus(r.data)).catch(() => {});
    api.get("/admin/modules").then((r) => setModules(r.data.items || r.data || [])).catch(() => {});
  }, []);

  const send = async () => {
    if (!testEmail) return;
    setSending(true);
    try {
      const r = await api.post("/admin/email/test", { to: testEmail });
      altioraToast.success(r.data.sent ? `Envoyé (${r.data.provider})` : `Mode log : ${r.data.provider}`);
    } catch (e) { altioraToast.error(e.response?.data?.detail || "Échec"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-[#0B2545]" />
          <div className="text-sm font-medium text-[#1F2937]">Service email transactionnel</div>
        </div>
        {emailStatus && (
          <div className="text-xs text-[#5B5B62] space-y-1">
            <div>Statut : {emailStatus.configured ? <span className="text-[#059669]">✓ Actif ({emailStatus.provider})</span> : <span className="text-[#C5A880]">⚠ Mode log (clé Resend absente)</span>}</div>
            <div>Expéditeur : <span className="font-mono text-[#1F2937]">{emailStatus.sender}</span></div>
            <div className="text-[11px] italic text-[#8B8B8F]">{emailStatus.hint}</div>
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <input
            data-testid="email-test-to"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="destinataire@exemple.com"
            className="flex-1 rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
          />
          <button onClick={send} disabled={sending || !testEmail} data-testid="email-test-btn" className="rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-50 text-white text-sm px-4 py-2">
            {sending ? "Envoi…" : "Envoyer un test"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <Cog className="h-4 w-4 text-[#0B2545]" />
          <div className="text-sm font-medium text-[#1F2937]">Feature flags · modules</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {modules.slice(0, 20).map((m) => (
            <div key={m.key} data-testid={`module-${m.key}`} className="flex items-center justify-between rounded-md border border-[#EBE8DE] px-3 py-2">
              <div>
                <div className="text-sm text-[#1F2937]">{m.name_fr || m.label || m.key}</div>
                <div className="text-[10px] font-mono text-[#8B8B8F]">{m.key}</div>
              </div>
              <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${m.enabled ? "bg-[#059669]/12 text-[#059669]" : "bg-[#E4E1D8] text-[#5B5B62]"}`}>
                {m.enabled ? "actif" : m.status || "off"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-[#E4E1D8] bg-white p-6">
        <div className="text-sm font-medium text-[#1F2937] mb-3">Liens rapides</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <Link to="/app/admin/docs" data-testid="quick-docs" className="text-xs inline-flex items-center justify-between rounded-md border border-[#EBE8DE] px-3 py-2 hover:border-[#0B2545]">
            <span>Documentation Hub</span> <ArrowUpRight className="h-3 w-3" />
          </Link>
          <Link to="/app/admin/users" data-testid="quick-users" className="text-xs inline-flex items-center justify-between rounded-md border border-[#EBE8DE] px-3 py-2 hover:border-[#0B2545]">
            <span>Détail utilisateurs</span> <ArrowUpRight className="h-3 w-3" />
          </Link>
          <Link to="/app/admin/legacy" data-testid="quick-legacy" className="text-xs inline-flex items-center justify-between rounded-md border border-[#EBE8DE] px-3 py-2 hover:border-[#0B2545]">
            <span>Ancien centre admin</span> <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// helpers
// ================================================================
function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <Icon className="h-4 w-4 text-[#5B5B62]" />
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">{title}</div>
    </div>
  );
}

function KPI({ label, value, sub, tone }) {
  const toneCls = tone === "good" ? "text-[#059669]" : tone === "warn" ? "text-[#C5A880]" : tone === "bad" ? "text-[#DC2626]" : "text-[#1F2937]";
  return (
    <div className="rounded-lg border border-[#E4E1D8] bg-white p-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">{label}</div>
      <div className={`mt-2 text-2xl md:text-3xl font-display tracking-tight ${toneCls}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] font-mono text-[#8B8B8F] mt-0.5">{sub}</div>}
    </div>
  );
}


// ================================================================
// VETTING — Validation queue for professional signups (P1)
// ================================================================
function VettingTab() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null); // "approve" | "reject" | "info"
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/network/admin/pending?status=${status}&limit=100`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const runAction = async () => {
    if (!selected || !action) return;
    if (action === "info" && note.trim().length < 8) {
      altioraToast.error("Merci d'indiquer les précisions à demander (min. 8 caractères).");
      return;
    }
    setBusy(true);
    try {
      const endpoint = { approve: "approve", reject: "reject", info: "request-info" }[action];
      await api.post(`/network/admin/${selected.id}/${endpoint}`, { note_fr: note || null });
      altioraToast.success({
        approve: "Profil approuvé — courriel envoyé au professionnel.",
        reject: "Profil refusé — courriel envoyé.",
        info: "Précisions demandées — courriel envoyé.",
      }[action]);
      setSelected(null);
      setAction(null);
      setNote("");
      await load();
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Action échouée");
    } finally {
      setBusy(false);
    }
  };

  const statusTabs = [
    { k: "pending",    label: "En attente",    tone: "warn" },
    { k: "needs_info", label: "Précisions",    tone: "warn" },
    { k: "approved",   label: "Approuvés",     tone: "good" },
    { k: "rejected",   label: "Refusés",       tone: "bad" },
    { k: "all",        label: "Tout",          tone: null },
  ];

  return (
    <div data-testid="admin-vetting-tab">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {statusTabs.map(({ k, label }) => (
          <button
            key={k}
            data-testid={`vetting-filter-${k}`}
            onClick={() => setStatus(k)}
            className={`px-4 py-2 text-sm font-medium rounded-md border-2 transition-colors ${
              status === k ? "bg-navy text-white border-navy" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-navy-200"
            }`}
          >
            {label}
            {counts[k] !== undefined && k !== "all" && (
              <span className={`ml-2 text-[10px] font-mono ${status === k ? "text-white/80" : "text-[#8B8B8F]"}`}>
                {counts[k]}
              </span>
            )}
          </button>
        ))}
        <button
          data-testid="vetting-refresh"
          onClick={load}
          className="ml-auto inline-flex items-center gap-2 border-2 border-[#E5E7EB] px-3 py-2 text-sm text-[#374151] hover:border-navy hover:text-navy rounded-md transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#5B5B62]"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-12 text-center text-sm text-[#5B5B62]">
          Aucun profil dans cette catégorie.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vetting-list">
          {items.map((m) => (
            <VettingCard key={m.id} member={m} onOpen={() => { setSelected(m); setAction(null); setNote(""); }} />
          ))}
        </div>
      )}

      {selected && (
        <VettingDrawer
          member={selected}
          action={action}
          setAction={setAction}
          note={note}
          setNote={setNote}
          busy={busy}
          onSubmit={runAction}
          onClose={() => { setSelected(null); setAction(null); setNote(""); }}
        />
      )}
    </div>
  );
}

function VettingCard({ member, onOpen }) {
  const statusTone = {
    pending:    "border-amber-300 bg-amber-50 text-amber-800",
    needs_info: "border-blue-300 bg-blue-50 text-blue-800",
    approved:   "border-emerald-300 bg-emerald-50 text-emerald-800",
    rejected:   "border-red-300 bg-red-50 text-red-800",
  }[member.status] || "border-[#E5E7EB] bg-[#F8F7F3] text-[#5B5B62]";

  return (
    <button
      onClick={onOpen}
      data-testid={`vetting-card-${member.id}`}
      className="text-left border-2 border-[#E5E7EB] bg-white rounded-lg p-5 hover:border-navy hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-xs font-mono uppercase tracking-[0.22em] text-navy">{member.role_network}</div>
          <div className="mt-1 font-display text-lg text-navy truncate">{member.display_name}</div>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm flex-shrink-0 ${statusTone}`}>
          {member.status || "—"}
        </span>
      </div>
      <div className="text-sm text-[#374151] leading-snug line-clamp-2 mb-2">{member.headline_fr}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#6B7280]">
        {member.organization && <span>🏢 {member.organization}</span>}
        {member.function_title && <span>💼 {member.function_title}</span>}
        {member.city && <span>📍 {member.city}</span>}
      </div>
      {member.email && (
        <div className="mt-2 text-[11px] text-[#8B8B8F] font-mono truncate">{member.email}</div>
      )}
    </button>
  );
}

function VettingDrawer({ member, action, setAction, note, setNote, busy, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vetting-drawer">
      <div className="absolute inset-0 bg-[#1F2937]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl border-l border-[#E5E7EB] flex flex-col">
        <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs font-mono uppercase tracking-[0.22em] text-navy">Validation professionnel</div>
            <div className="mt-1 font-display text-2xl text-navy truncate">{member.display_name}</div>
            <div className="mt-1 text-sm text-[#5B5B62]">{member.role_network} · {member.status}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[#5B5B62] hover:text-[#1F2937]" data-testid="vetting-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <DetailRow label="Intitulé">{member.headline_fr}</DetailRow>
          {member.bio_fr && <DetailRow label="Bio">{member.bio_fr}</DetailRow>}
          {member.organization && <DetailRow label="Organisation">{member.organization}</DetailRow>}
          {member.function_title && <DetailRow label="Fonction">{member.function_title}</DetailRow>}
          {member.linkedin_url && (
            <DetailRow label="LinkedIn">
              <a href={member.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy hover:underline">
                {member.linkedin_url} <ExternalLink className="h-3 w-3" />
              </a>
            </DetailRow>
          )}
          {member.website_url && (
            <DetailRow label="Site web">
              <a href={member.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy hover:underline">
                {member.website_url} <ExternalLink className="h-3 w-3" />
              </a>
            </DetailRow>
          )}
          {member.join_reason_fr && (
            <DetailRow label="Raison de rejoindre ALTIORA">
              <div className="whitespace-pre-wrap">{member.join_reason_fr}</div>
            </DetailRow>
          )}
          {(member.expertises?.length || member.sectors?.length) && (
            <DetailRow label="Expertises · Secteurs">
              <div className="flex flex-wrap gap-1.5">
                {(member.expertises || []).map((e) => <span key={e} className="px-2 py-0.5 rounded border border-navy/25 bg-navy-50 text-navy text-xs">{e}</span>)}
                {(member.sectors || []).map((s) => <span key={s} className="px-2 py-0.5 rounded border border-[#E5E7EB] bg-[#F8F7F3] text-[#5B5B62] text-xs">{s}</span>)}
              </div>
            </DetailRow>
          )}
          {member.city && <DetailRow label="Localisation">{member.city}{member.country ? ` · ${member.country}` : ""}</DetailRow>}
          {member.email && <DetailRow label="Courriel">{member.email}</DetailRow>}
          {member.status_note_fr && (
            <DetailRow label="Note administrateur">
              <div className="whitespace-pre-wrap text-[#5B5B62] italic">{member.status_note_fr}</div>
            </DetailRow>
          )}
        </div>

        <div className="border-t border-[#E5E7EB] bg-[#F8F7F3] px-6 py-4 space-y-3">
          {action && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.22em] text-navy mb-1.5">
                {action === "approve" && "Note d'approbation (facultative)"}
                {action === "reject" && "Motif de refus (recommandé)"}
                {action === "info" && "Précisions demandées (min. 8 caractères) *"}
              </label>
              <textarea
                data-testid="vetting-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={
                  action === "approve" ? "Ex : Bienvenue ! N'hésitez pas à contacter l'équipe pour toute question." :
                  action === "reject"  ? "Ex : Profil hors du périmètre pilote actuel." :
                                        "Ex : Merci de confirmer votre affiliation via un email professionnel."
                }
                className="w-full px-3 py-2 border-2 border-[#E5E7EB] focus:border-navy rounded-md text-sm outline-none transition-colors bg-white"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="vetting-btn-approve"
              onClick={() => action === "approve" ? onSubmit() : setAction("approve")}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors shadow-sm"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
              {action === "approve" ? (busy ? "Envoi…" : "Confirmer approbation") : "Approuver"}
            </button>
            <button
              data-testid="vetting-btn-info"
              onClick={() => action === "info" ? onSubmit() : setAction("info")}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors shadow-sm"
            >
              <MessageSquare className="h-4 w-4" strokeWidth={2} />
              {action === "info" ? (busy ? "Envoi…" : "Envoyer la demande") : "Demander des précisions"}
            </button>
            <button
              data-testid="vetting-btn-reject"
              onClick={() => action === "reject" ? onSubmit() : setAction("reject")}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors shadow-sm"
            >
              <X className="h-4 w-4" strokeWidth={2} />
              {action === "reject" ? (busy ? "Envoi…" : "Confirmer refus") : "Refuser"}
            </button>
            {action && (
              <button
                onClick={() => { setAction(null); setNote(""); }}
                className="text-sm text-[#5B5B62] hover:text-navy px-2 py-2"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-[0.22em] text-navy mb-1">{label}</div>
      <div className="text-sm text-[#1F2937] leading-relaxed">{children}</div>
    </div>
  );
}

